import { isTemperatureRuleComplete } from './temperatureProfiles';

export const TEMPERATURE_EQUIPMENT_TYPES = [
  { value: 'FRIDGE', label: 'Frigorifero', numberLabel: 'Frigo', kind: 'FRIDGE' },
  { value: 'FREEZER', label: 'Freezer / Congelatore', numberLabel: 'Freezer', kind: 'FREEZER' },
  { value: 'COLD_ROOM', label: 'Cella frigo', numberLabel: 'Cella frigo', kind: 'COLD_ROOM' },
  {
    value: 'COLD_FREEZER',
    label: 'Cella freezer',
    numberLabel: 'Cella freezer',
    kind: 'FREEZER',
    labelPrefix: 'Cella freezer',
  },
];

const COMPANY_PROPOSAL_REFERENCE = 'Proposta aziendale iniziale, modificabile e non universale.';

const DEFAULT_TARGETS = {
  FRIDGE: { min: '2', max: '5' },
  FREEZER: { min: '-22', max: '-18' },
  COLD_ROOM: { min: '2', max: '5' },
};

function ruleForCompleteness(rule) {
  return {
    ...rule,
    check_times_text: rule.check_times_text
      ?? (rule.check_times_local || []).join(', '),
    late_after_minutes: rule.late_after_minutes ?? '',
    verification_after_minutes: rule.verification_after_minutes ?? '',
    corrective_instructions: rule.corrective_instructions || '',
    operational_target_min_temperature_c: rule.operational_target_min_temperature_c ?? '',
    operational_target_max_temperature_c: rule.operational_target_max_temperature_c ?? '',
    min_temperature_c: rule.min_temperature_c ?? '',
    max_temperature_c: rule.max_temperature_c ?? '',
  };
}

export function isActiveTemperatureProcedure(procedure) {
  if (typeof procedure?.is_active === 'boolean') return procedure.is_active;
  if (typeof procedure?.active === 'boolean') return procedure.active;
  if (procedure?.status) return String(procedure.status).toUpperCase() === 'ACTIVE';
  // The backend template-list endpoint already filters to active company templates.
  return true;
}

function procedureSignature(rule) {
  const normalized = ruleForCompleteness(rule);
  return JSON.stringify({
    limit_mode: normalized.limit_mode,
    min_temperature_c: normalized.min_temperature_c,
    max_temperature_c: normalized.max_temperature_c,
    tolerance_c: normalized.tolerance_c ?? null,
    profile_code: normalized.profile_code ?? null,
    operational_target_min_temperature_c: normalized.operational_target_min_temperature_c,
    operational_target_max_temperature_c: normalized.operational_target_max_temperature_c,
    limit_basis: normalized.limit_basis ?? null,
    basis_reference: normalized.basis_reference ?? null,
    check_times_local: normalized.check_times_text.split(',').map((value) => value.trim()),
    late_after_minutes: normalized.late_after_minutes,
    instructions: normalized.instructions ?? null,
    anomaly_note_required: normalized.anomaly_note_required ?? true,
    measurement_method: normalized.measurement_method,
    corrective_instructions: normalized.corrective_instructions,
    verification_after_minutes: normalized.verification_after_minutes,
  });
}

export function resolveTemperatureProcedure(equipmentKind, existingRules = [], procedures = []) {
  const matchingTemplates = procedures.filter((procedure) => (
    procedure.equipment_kind === equipmentKind
    && !procedure.profile_code
    && isActiveTemperatureProcedure(procedure)
    && isTemperatureRuleComplete(ruleForCompleteness(procedure))
  ));
  const matchingRules = existingRules.filter((rule) => (
    rule.equipment_kind === equipmentKind
    && !rule.profile_code
    && isTemperatureRuleComplete(ruleForCompleteness(rule))
  ));

  const distinctRules = new Map(
    matchingRules.map((rule) => [procedureSignature(rule), rule]),
  );
  if (distinctRules.size > 1) return { ok: false, reason: 'ambiguous' };
  if (distinctRules.size === 1) {
    return { ok: true, procedure: distinctRules.values().next().value, source: 'draft' };
  }
  if (matchingTemplates.length > 1) return { ok: false, reason: 'ambiguous' };
  if (matchingTemplates.length === 1) {
    return { ok: true, procedure: matchingTemplates[0], source: 'company' };
  }
  return { ok: false, reason: 'missing' };
}

export function applyTemperatureStartingValues(rule, values) {
  const minimum = String(values.min);
  const maximum = String(values.max);
  const freezer = rule.equipment_kind === 'FREEZER';

  return {
    ...rule,
    limit_mode: freezer ? 'MAX_ONLY' : 'RANGE',
    min_temperature_c: freezer ? '' : minimum,
    max_temperature_c: maximum,
    operational_target_min_temperature_c: minimum,
    operational_target_max_temperature_c: maximum,
    limit_basis: 'COMPANY_STANDARD',
    basis_reference: COMPANY_PROPOSAL_REFERENCE,
  };
}

export function equipmentTypeForDraft(type) {
  return TEMPERATURE_EQUIPMENT_TYPES.find((item) => item.value === type)
    || TEMPERATURE_EQUIPMENT_TYPES[0];
}

export function nextTemperatureEquipmentNumber(type, equipment = []) {
  const selected = equipmentTypeForDraft(type);
  const maximum = equipment
    .filter((item) => item.kind === selected.kind)
    .map((item) => {
      const number = String(item.number || '').match(/(\d+)$/)?.[1];
      return Number.parseInt(number, 10);
    })
    .filter(Number.isFinite)
    .reduce((highest, value) => Math.max(highest, value), 0);
  return String(maximum + 1);
}

export function temperatureEquipmentForDisplay(equipment = []) {
  const groups = new Map();
  equipment.forEach((item, index) => {
    if (!item?.standalone) return;
    const kind = item.kind || item.equipment_kind || 'UNKNOWN';
    if (!groups.has(kind)) groups.set(kind, []);
    groups.get(kind).push({ item, index });
  });

  const displayNumbers = new Map();
  groups.forEach((group) => {
    group.sort((left, right) => {
      const leftNumber = Number.parseInt(String(left.item.number ?? '').match(/(\d+)$/)?.[1], 10);
      const rightNumber = Number.parseInt(String(right.item.number ?? '').match(/(\d+)$/)?.[1], 10);
      if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftNumber !== rightNumber) {
        return leftNumber - rightNumber;
      }
      return left.index - right.index;
    });
    group.forEach(({ index }, sequence) => displayNumbers.set(index, sequence + 1));
  });

  return equipment.map((item, index) => ({
    ...item,
    displayNumber: displayNumbers.get(index) ?? item.number,
  }));
}

function normalizedTemperatureEquipmentLabel(kind, label) {
  const rawLabel = String(label || '').trim();
  const isColdFreezer = kind === 'FREEZER' && /^Cella freezer\b/i.test(rawLabel);
  let suffix = isColdFreezer
    ? rawLabel.replace(/^Cella freezer(?:\s*[·—–:-]\s*)?/i, '').trim()
    : rawLabel;
  const equipmentWords = '(?:frigo(?:rifero)?|freezer|congelatore|cella(?:\\s+(?:frigo|freezer))?)';
  const duplicatePrefixes = [
    new RegExp(`^${equipmentWords}\\s*#?\\s*\\d+\\s*(?:[·—–:-]\\s*)?`, 'i'),
    new RegExp(`^\\d+\\s*${equipmentWords}(?:\\s*#?\\s*\\d+)?\\s*(?:[·—–:-]\\s*)?`, 'i'),
  ];
  for (let index = 0; index < 3; index += 1) {
    const clean = duplicatePrefixes.reduce(
      (value, pattern) => value.replace(pattern, '').trim(),
      suffix,
    );
    if (clean === suffix) break;
    suffix = clean;
  }
  if (/^\d+$/.test(suffix)) suffix = '';
  if (suffix) suffix = `${suffix[0].toLocaleUpperCase('it-IT')}${suffix.slice(1)}`;
  return { isColdFreezer, suffix };
}

export function temperatureEquipmentLabelDescription(kind, label) {
  return normalizedTemperatureEquipmentLabel(kind, label).suffix;
}

export function temperatureEquipmentDisplayName(kind, number, label) {
  const { isColdFreezer, suffix } = normalizedTemperatureEquipmentLabel(kind, label);
  const name = isColdFreezer ? 'Cella freezer' : ({
    FRIDGE: 'Frigo',
    FREEZER: 'Freezer',
    COLD_ROOM: 'Cella frigo',
  }[kind] || 'Attrezzatura');
  return `${name} ${number}${suffix ? ` — ${suffix}` : ''}`;
}

export function applyTemperatureCompanyTemplate(rule, template) {
  const freezer = rule.equipment_kind === 'FREEZER';
  return {
    ...rule,
    ...(freezer ? {} : {
      limit_mode: template.limit_mode,
      min_temperature_c: template.min_temperature_c ?? '',
      max_temperature_c: template.max_temperature_c ?? '',
    }),
    ...(freezer ? {
      limit_mode: 'MAX_ONLY',
      min_temperature_c: '',
      max_temperature_c: '-18',
    } : {}),
    profile_code: template.profile_code || rule.profile_code || null,
    limit_basis: template.limit_basis || rule.limit_basis || null,
    basis_reference: template.basis_reference || rule.basis_reference || '',
    tolerance_c: template.tolerance_c ?? rule.tolerance_c,
    check_times_text: (template.check_times_local || []).join(', '),
    check_times_local: template.check_times_local || [],
    late_after_minutes: template.late_after_minutes ?? '',
    instructions: template.instructions || '',
    anomaly_note_required: template.anomaly_note_required ?? true,
    measurement_method: template.measurement_method || 'DISPLAY',
    corrective_instructions: template.corrective_instructions || '',
    verification_after_minutes: template.verification_after_minutes ?? '',
    appliedCompanyProcedure: template.name || '',
  };
}

export function createTemperatureRuleProposal(equipment, existingRules = [], templates = []) {
  const isFreezer = equipment.kind === 'FREEZER';
  const isFridge = equipment.kind === 'FRIDGE' || equipment.kind === 'COLD_ROOM';
  const targets = DEFAULT_TARGETS[equipment.kind] || { min: '', max: '' };
  const rule = {
    equipment_id: equipment.id,
    equipment_kind: equipment.kind,
    equipment_number: equipment.number,
    equipment_label: equipment.label || '',
    limit_mode: isFreezer ? 'MAX_ONLY' : 'RANGE',
    min_temperature_c: isFreezer ? '' : isFridge ? '2' : '',
    max_temperature_c: isFreezer ? '-18' : isFridge ? '5' : '',
    tolerance_c: '',
    profile_code: null,
    operational_target_min_temperature_c: targets.min,
    operational_target_max_temperature_c: targets.max,
    limit_basis: 'COMPANY_STANDARD',
    basis_reference: COMPANY_PROPOSAL_REFERENCE,
    check_times_text: '',
    late_after_minutes: '',
    instructions: '',
    anomaly_note_required: true,
    measurement_method: 'DISPLAY',
    corrective_instructions: '',
    verification_after_minutes: '',
    appliedCompanyProcedure: '',
    availableCompanyProcedures: [],
    isNewProposal: true,
  };
  const resolved = resolveTemperatureProcedure(equipment.kind, existingRules, templates);
  return resolved.ok ? applyTemperatureCompanyTemplate(rule, resolved.procedure) : rule;
}

